import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Eye, Mail, UserX, Search, Flame } from "lucide-react";
import { CURRICULUM } from "@/lib/curriculum";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOD = Object.fromEntries(CURRICULUM.map((m) => [m.id, m]));

const fmtAgo = (iso) => {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days === 0 ? "Today" : days === 1 ? "Yesterday" : days < 30 ? `${days}d ago` : new Date(iso).toLocaleDateString();
};

const labelFor = (it) => {
  if (it.kind === "module") { const m = MOD[it.id]; return m ? `${m.n} · ${m.title}` : `Module ${it.id}`; }
  if (it.kind === "book") return it.label || `Book · ${it.id}`;
  return it.label || it.id;
};

const StatCard = ({ icon: Icon, value, label, tone }) => (
  <div className="tacc-card p-5" data-testid={`interest-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <Icon size={16} className={tone || "text-[#B45309]"} />
    <div className="font-display text-3xl md:text-4xl leading-none mt-3">{value}</div>
    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] mt-2">{label}</div>
  </div>
);

export const AdminInterestTab = () => {
  const [data, setData] = useState(null);
  const [onlyContactable, setOnlyContactable] = useState(false);
  const [onlyWarm, setOnlyWarm] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/interest`, { params: { limit: 300 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ totals: [], visitors: [], summary: {} }));
  }, []);

  if (!data) return <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#78716C] animate-pulse">Loading interest…</p>;

  const s = data.summary || {};
  const maxLooks = data.totals?.[0]?.looks || 1;
  let visitors = data.visitors || [];
  if (onlyContactable) visitors = visitors.filter((v) => v.contactable);
  if (onlyWarm) visitors = visitors.filter((v) => v.warm_lead);
  if (q.trim()) {
    const rx = q.trim().toLowerCase();
    visitors = visitors.filter((v) =>
      (v.email || "").toLowerCase().includes(rx) ||
      (v.name || "").toLowerCase().includes(rx) ||
      (v.items || []).some((it) => labelFor(it).toLowerCase().includes(rx)));
  }

  return (
    <div className="space-y-8" data-testid="admin-interest-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} value={s.total_visitors ?? 0} label="Visitors" />
        <StatCard icon={Eye} value={s.total_looks ?? 0} label="Total looks" />
        <StatCard icon={Flame} value={s.warm_leads ?? 0} label="Warm leads" tone="text-[#B45309]" />
        <StatCard icon={Mail} value={s.contactable_visitors ?? 0} label="Contactable" tone="text-emerald-400" />
        <StatCard icon={UserX} value={s.anonymous_visitors ?? 0} label="Anonymous" tone="text-[#78716C]" />
      </div>

      <div className="tacc-card p-6" data-testid="interest-totals">
        <h3 className="font-display text-2xl tracking-tight mb-5">Most looked-at products &amp; services</h3>
        <div className="space-y-2.5">
          {(data.totals || []).slice(0, 12).map((t) => (
            <div key={`${t.kind}-${t.id}`} className="flex items-center gap-4" data-testid={`interest-total-${t.kind}-${t.id}`}>
              <div className="w-56 shrink-0 text-sm truncate" title={labelFor(t)}>{labelFor(t)}</div>
              <div className="flex-1 h-2 bg-black/5 rounded-full overflow-hidden">
                <div className="h-full bg-[#F59E0B] rounded-full" style={{ width: `${Math.max(4, (t.looks / maxLooks) * 100)}%` }} />
              </div>
              <div className="w-32 shrink-0 text-right font-mono text-[11px] text-[#78716C]">{t.looks} looks · {t.unique_visitors} ppl</div>
            </div>
          ))}
          {(!data.totals || data.totals.length === 0) && (
            <p className="text-[#78716C] text-sm">No looks logged yet. As people browse your products &amp; services, they'll appear here.</p>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <label className="flex items-center gap-3 border border-[#E7DFD5] px-4 py-3 w-full md:w-96 focus-within:border-[#F59E0B]">
          <Search size={14} className="text-[#78716C]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name, or product…" data-testid="interest-search"
                 className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-[#555]" />
        </label>
        <div className="flex items-center gap-2">
          <button onClick={() => setOnlyWarm((v) => !v)} data-testid="interest-warm-toggle" aria-pressed={onlyWarm}
                  className={`px-4 py-2 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] border transition-colors inline-flex items-center gap-1.5 ${onlyWarm ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-[#E7DFD5] text-[#44403C] hover:border-[#F59E0B]"}`}>
            <Flame size={12} /> Warm leads only
          </button>
          <button onClick={() => setOnlyContactable((v) => !v)} data-testid="interest-contactable-toggle" aria-pressed={onlyContactable}
                  className={`px-4 py-2 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] border transition-colors ${onlyContactable ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-[#E7DFD5] text-[#44403C] hover:border-[#F59E0B]"}`}>
            Contactable only
          </button>
        </div>
      </div>

      <div className="tacc-card overflow-x-auto" data-testid="interest-visitors-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] border-b border-[#E7DFD5]">
              <th className="p-4">Visitor</th><th className="p-4">Looked at</th><th className="p-4">Looks</th><th className="p-4">Last seen</th><th className="p-4 text-right">Contact</th>
            </tr>
          </thead>
          <tbody>
            {visitors.map((v) => (
              <tr key={v.visitor_id} className="border-b border-[#E7DFD5] hover:bg-black/5" data-testid={`interest-row-${v.visitor_id}`}>
                <td className="p-4">
                  {v.email ? (
                    <>
                      <div className="text-[#1C1917]">{v.name || v.email}</div>
                      <div className="text-xs text-[#78716C]">{v.email}</div>
                    </>
                  ) : (
                    <div className="text-[#78716C]">Anonymous <span className="font-mono text-[10px] text-[#555]">· {String(v.visitor_id).slice(-6)}</span></div>
                  )}
                  {v.warm_lead && (
                    <span data-testid={`interest-warm-${v.visitor_id}`} className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#B45309] text-[9px] font-mono tracking-[0.15em] uppercase">
                      <Flame size={10} /> Warm lead
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1.5 max-w-[420px]" data-testid={`interest-items-${v.visitor_id}`}>
                    {(v.items || []).slice(0, 6).map((it) => (
                      <span key={`${it.kind}-${it.id}`} title={labelFor(it)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#F59E0B]/40 text-[#B45309] text-[10px] font-mono">
                        {labelFor(it)}{it.count > 1 && <span className="text-[#78716C]">×{it.count}</span>}
                      </span>
                    ))}
                    {(v.items || []).length > 6 && <span className="text-[10px] text-[#78716C] font-mono self-center">+{v.items.length - 6}</span>}
                  </div>
                </td>
                <td className="p-4 text-[#B45309]">{v.total_looks}</td>
                <td className="p-4 text-[#78716C] whitespace-nowrap">{fmtAgo(v.last_seen)}</td>
                <td className="p-4 text-right">
                  {v.email ? (
                    <a href={`mailto:${v.email}`} data-testid={`interest-email-${v.visitor_id}`} className="btn-outline text-xs !py-1.5 !px-3">
                      <Mail size={13} /> Email
                    </a>
                  ) : <span className="text-[#555] font-mono text-[9px] tracking-[0.2em] uppercase">no email yet</span>}
                </td>
              </tr>
            ))}
            {visitors.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#78716C]">No visitors match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
